import { Plus, Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContentBlock } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useBlocks } from '../hooks/use-blocks';

import { BlockKindBadge, BlockScopeBadge, ContentStatusBadge } from './content-badges';

export interface AddSectionBlocksDialogProps {
  storeId: string;
  /** Block ids already placed; shown checked and not selectable again. */
  excludeIds: ReadonlySet<string>;
  onAdd: (rows: ContentBlock[]) => void;
  disabled?: boolean;
}

/** Multi-select over the store's own blocks and the organisation's shared ones. */
export function AddSectionBlocksDialog({
  storeId,
  excludeIds,
  onAdd,
  disabled,
}: AddSectionBlocksDialogProps) {
  const { t } = useTranslation(['content', 'common']);
  const idPrefix = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Map<string, ContentBlock>>(new Map());

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  const query = useBlocks(storeId, {
    scope: 'all',
    limit: 50,
    ...(debounced ? { q: debounced } : {}),
  });
  const candidates = query.data?.items ?? [];

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQ('');
      setSelected(new Map());
    }
  };

  const toggle = (row: ContentBlock, checked: boolean) =>
    setSelected((current) => {
      const next = new Map(current);
      if (checked) next.set(row.id, row);
      else next.delete(row.id);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          data-tour="content.section-editor.add-blocks"
        >
          <Plus aria-hidden />
          {t('content:sectionBlocks.add')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('content:sectionBlocks.dialog.title')}</DialogTitle>
          <DialogDescription>{t('content:sectionBlocks.dialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('content:sectionBlocks.dialog.searchPlaceholder')}
              aria-label={t('content:sectionBlocks.dialog.search')}
              className="pl-8"
              autoFocus
            />
          </div>
          <div
            className="max-h-72 overflow-y-auto rounded-md border"
            role="group"
            aria-label={t('content:sectionBlocks.dialog.results')}
          >
            {query.isPending ? (
              <div className="flex flex-col gap-2 p-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-8" />
                ))}
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-muted-foreground p-4 text-center text-sm">
                {t('content:sectionBlocks.dialog.noResults')}
              </p>
            ) : (
              <ul className="divide-y">
                {candidates.map((row) => {
                  const id = `${idPrefix}-${row.id}`;
                  const already = excludeIds.has(row.id);
                  const checked = already || selected.has(row.id);
                  return (
                    <li key={row.id}>
                      <label
                        htmlFor={id}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 text-sm',
                          already ? 'text-muted-foreground' : 'cursor-pointer',
                          checked && !already && 'bg-primary-tint/40',
                        )}
                      >
                        <Checkbox
                          id={id}
                          checked={checked}
                          disabled={already}
                          aria-label={row.name}
                          onCheckedChange={(value) => toggle(row, value === true)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{row.name}</span>
                          <span className="text-muted-foreground block truncate font-mono text-[11px]">
                            {row.key}
                          </span>
                        </span>
                        {already ? (
                          <span className="text-muted-foreground text-xs">
                            {t('content:sectionBlocks.dialog.alreadyAdded')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <BlockKindBadge kind={row.kind} />
                            <BlockScopeBadge scope={row.scope} />
                            <ContentStatusBadge status={row.status} />
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter className="items-center sm:justify-between">
            <Label className="text-muted-foreground text-xs font-normal">
              {t('content:sectionBlocks.dialog.selectedCount', { count: selected.size })}
            </Label>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                type="button"
                disabled={selected.size === 0}
                onClick={() => {
                  onAdd([...selected.values()]);
                  onOpenChange(false);
                }}
              >
                {t('content:sectionBlocks.dialog.submit', { count: selected.size })}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
